import React from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Card, CardFooter, CardBody } from 'reactstrap';
import { Button, FormAlerts, useFeatures, Form } from '@cordata/buffet';
import { useHistory, Prompt } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authorizedPyInstance as py } from '~/utils';

import Fields from './Fields';

const validationSchema = Yup.object().shape({
  eventDate: Yup.date().required(),
  fromProvider: Yup.mixed().required(),
  to: Yup.mixed().required(),
  status: Yup.mixed().required(),
  priority: Yup.mixed().required(),
  specialty: Yup.mixed().nullable(),
  reason: Yup.mixed().nullable(),

  // Conditional validation based on referral type (facility or division only)
  toFacility: Yup.mixed().when('to', {
    is: 'facility',
    then: Yup.mixed().required(),
  }),
  toDivision: Yup.mixed().when('to', {
    is: 'division',
    then: Yup.mixed().required(),
  }),
});
const initialValues = ({
  eventDate: new Date(),

});

// PYTHON PUBLIC API: Single API call with automatic email and IBR handling

const handleSubmit = async (values, { setSubmitting, setErrors, resetForm }, history) => {
  try {
    // Create manual OBR using CIT endpoint (reuses existing CIT logic)
    const obrPayload = {
      patient_uid: values.patientUid,
      referred_to: values.to === 'facility' ? 'Facility' : values.to === 'division' ? 'Division' : 'Provider',
      event_date: values.eventDate?.toISOString?.() || values.eventDate,
      note: values.notes || 'Manual referral created by user',
      officer: values.fromProvider?.displayName || 'Manual Referral',
      from_provider: values.fromProvider?.id || values.fromProvider,
      specialty: values.specialty?.id || values.specialty,
      priority: values.priority?.id || values.priority,
      reason: values.reason?.id || values.reason,
      status: values.status?.id || values.status,
      // Add specific referral target
      ...(values.to === 'facility' && { facility_uid: values.toFacility?.id || values.toFacility }),
      ...(values.to === 'division' && { division_uid: values.toDivision?.id || values.toDivision }),
      ...(values.to === 'provider' && { provider_uid: values.toProvider?.id || values.toProvider }),
    };

    const response = await py.post(
      '/api/data/public/cit/manual-obr',
      obrPayload,
    );
    // Success notifications based on referral type
    if (values.to === 'facility') {
      if (response.data.email_sent) {
        toast.success('Outbound referral created! Email sent to facility with CIT contact sheet.');
      } else if (response.data.email_error) {
        toast.success('Outbound referral created successfully! However, email could not be sent to facility.');
      } else {
        toast.success('Outbound referral created successfully!');
      }
    } else {
      toast.success('Outbound referral created successfully!');
    }

    resetForm({ ...values });

    if (values.patientUid) {
      // Navigate back to patient's outbound referrals page if we have patient context
      history.push(`/patients/${values.patientUid}/outbound-referrals`);
    } else {
      // Navigate back to CIT dashboard Mental Health Referrals tab
      history.push('/home?tab=3');
    }
  } catch (error) {
    console.error('Error creating outbound referral:', error);
    setErrors({ server: error.response?.data?.error || ['An error occurred'] });
  } finally {
    setSubmitting(false);
  }
};

const OutboundReferralForm = ({ data, disabled }) => {
  const history = useHistory();
  const { hasAppCit } = useFeatures();

  let to;

  if (data.toDivision) {
    to = 'division';
  } else if (data.toFacility) {
    to = 'facility';
  } else if (data.toProvider) {
    to = 'provider';
  }

  return (
    <Card>
      <Form
        displayName="OutboundReferralForm"
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={{
          ...initialValues,
          ...data,
          to,
        }}
        onSubmit={(values, formikBag) => handleSubmit(values, formikBag, history)}
      >
        {({
          isSubmitting,
          submitCount,
          errors,
          dirty,
        }) => (
          <>
            <CardBody>
              {submitCount >= 1 && errors && <FormAlerts errors={errors} />}
              <Fields disabled={disabled} hasAppCit={hasAppCit} />
            </CardBody>

            <CardFooter className="d-flex">
              <div className="ml-auto">
                <Button
                  type="cancel"
                  disabled={isSubmitting}
                  onClick={() => history.goBack()}
                >
                  Cancel
                </Button>

                <Button type="submit" disabled={disabled || isSubmitting}>
                  {isSubmitting ? 'Working ...' : 'Save'}
                </Button>
              </div>
            </CardFooter>

            <Prompt
              when={dirty}
              message="You have unsaved changes. These will be lost if you leave this page."
            />
          </>
        )}
      </Form>
    </Card>
  );
};

OutboundReferralForm.propTypes = {
  data: PropTypes.shape(),
  disabled: PropTypes.bool,
};

OutboundReferralForm.defaultProps = {
  data: {},
  disabled: false,
};

export default OutboundReferralForm;
